import type { Catalog, MembershipClient, Summary } from './types';
import example from './membership-example.json';
const plans: Catalog['plans'] = [
  { id: 'starter', name: 'Starter', monthly_amount_cents: 1990, monthly_stars: 2000, monthly_discount_bps: 500 },
  { id: 'plus', name: 'Plus', monthly_amount_cents: 3900, monthly_stars: 4000, monthly_discount_bps: 500 },
  { id: 'pro', name: 'Pro', monthly_amount_cents: 9500, monthly_stars: 6000, monthly_discount_bps: 800 },
  { id: 'max', name: 'Max', monthly_amount_cents: 19900, monthly_stars: 12000, monthly_discount_bps: 800 },
];
/** One client per mounted demo session. No fetch, auth mutation, provider or shared storage. */
export function createMembershipDemo(): MembershipClient {
  const content = {
    id: 'star-sprint-report',
    kind: 'resource' as const,
    content_id: 'star-sprint-report',
    minimum_tier: 'plus' as const,
    title: 'Star Sprint — complete project and parent report',
    published: true,
    body_md: example.report_md,
  };
  let state: Summary = {
    wallet_balance: 4488,
    membership_stars: 3988,
    recharge_stars: 500,
    membership_allowance: 4000,
    membership_expires_at: '2026-11-01T00:00:00.000Z',
    allowance_events: [],
    next_stars_at: null,
    next_payment_at: '2026-11-01T00:00:00.000Z',
    status: 'active',
    tier: 'plus',
    cycle: 'monthly',
    effective_tier: 'plus',
    period_end: '2026-11-01T00:00:00.000Z',
    anchor_at: '2026-10-01T00:00:00.000Z',
    cancel_at_period_end: false,
    pending_change: null,
    content: [content],
    grants: [
      {
        id: 'fictional-grant-oct',
        cycle_start: '2026-10-01T00:00:00.000Z',
        stars: 4000,
        created_at: '2026-10-01T00:00:00.000Z',
      },
    ],
    invoices: [
      {
        invoice_id: 'fictional-paid-oct',
        amount_cents: 3900,
        currency: 'AUD',
        starts_at: '2026-10-01T00:00:00.000Z',
        ends_at: '2026-11-01T00:00:00.000Z',
        invoice_url: null,
        pdf_url: null,
      },
    ],
  };
  return {
    previewNextPaidMonth: async () => {
      const boundary = state.period_end!;
      const expires = new Date(boundary); expires.setUTCMonth(expires.getUTCMonth()+1);
      const tier = state.pending_change?.tier ?? state.tier;
      const plan = plans.find(p=>p.id===tier)!;
      const renews = !state.cancel_at_period_end;
      const amount = renews ? plan.monthly_stars : 0;
      const expired = state.membership_stars ?? 0;
      state = {...state, tier:renews?tier:state.tier, effective_tier:renews?tier:null, status:renews?'active':'expired',
        membership_stars:amount, membership_allowance:amount, recharge_stars:state.recharge_stars,
        membership_expires_at:renews?expires.toISOString():boundary, wallet_balance:amount+(state.recharge_stars??0),
        period_end:renews?expires.toISOString():boundary, next_payment_at:renews?expires.toISOString():null, pending_change:null,
        allowance_events:[...(state.allowance_events??[]),{reason:'Unused membership Stars expired; recharge unchanged',delta_stars:-expired,created_at:boundary}],
        grants:renews?[...state.grants,{id:'fictional-reset-'+boundary,cycle_start:boundary,stars:amount,created_at:boundary}]:state.grants,
        invoices:renews?[...state.invoices,{invoice_id:'fictional-invoice-'+boundary,amount_cents:plan.monthly_amount_cents,currency:'AUD',starts_at:boundary,ends_at:expires.toISOString(),invoice_url:null,pdf_url:null}]:state.invoices};
    },
    catalog: async () => ({
      version: '2026-10-21.v5',
      revision: 0,
      quote_token: 'isolated-demo-no-payment',
      currency: 'AUD',
      plans: structuredClone(plans),
      purchase_available: true,
      terms_url: null,
      tax_included: null,
      content: [content],
    }),
    current: async () => structuredClone(state),
    resource: async (id) => {
      if (id !== content.id) throw new Error('This resource is not in the isolated example.');
      return structuredClone(content);
    },
    action: async (kind, body) => {
      if (kind === 'cancel') {
        state = {
          ...state,
          status: 'cancel_at_period_end',
          next_payment_at: null,
          cancel_at_period_end: true,
        };
        return { status: 'succeeded' };
      }
      if (kind === 'resume') {
        state = {
          ...state,
          status: 'active',
          next_payment_at: state.period_end,
          cancel_at_period_end: false,
        };
        return { status: 'succeeded' };
      }
      if (kind === 'change') {
        const input = body as { tier: Summary['tier']; cycle: Summary['cycle'] };
        if (!input.tier || !plans.some((p) => p.id === input.tier) || input.cycle !== 'monthly') throw new Error('Choose a plan.');
        state = {
          ...state,
          pending_change: {
            tier: input.tier,
            cycle: input.cycle,
            effective_at: '2026-11-01T00:00:00.000Z',
          },
        };
        return { status: 'succeeded' };
      }
      if (kind === 'reconcile') return { status: 'succeeded' };
      throw new Error('Checkout is unavailable in this Demo. No payment is created.');
    },
  };
}
